import { useParams, Link, Navigate } from "react-router-dom";
import { useState, useMemo } from "react";
import { categories, articles } from "@/mocks/data";
import Breadcrumb from "@/components/base/Breadcrumb";

const articleCategoryImages: Record<string, string> = {
  "shouji-zipai-ru-men-jiqiao": "https://readdy.ai/api/search-image?query=Elegant%20minimal%20photography%20studio%20setup%20with%20warm%20golden%20amber%20lighting%2C%20clean%20aesthetic%20composition%2C%20soft%20natural%20textures%2C%20editorial%20style%2C%20modern%20smartphone%20on%20wooden%20surface&width=800&height=500&seq=cat-article-basics&orientation=landscape",
  "guochan-zipai-shenqi-tuijian": "https://readdy.ai/api/search-image?query=Modern%20Chinese%20smartphone%20displayed%20elegantly%20on%20a%20minimal%20wooden%20desk%2C%20warm%20golden%20lighting%2C%20clean%20product%20photography%20style%2C%20refined%20editorial%20look&width=800&height=500&seq=cat-article-devices&orientation=landscape",
  "zipai-goutu-fangfa-san-fen-fa": "https://readdy.ai/api/search-image?query=Artistic%20photography%20composition%20concept%20with%20grid%20lines%2C%20warm%20golden%20toned%20abstract%20background%2C%20elegant%20minimalist%20design%2C%20editorial%20photography%20aesthetic&width=800&height=500&seq=cat-article-compose&orientation=landscape",
  "shouji-xiu-tu-app-tui-jian": "https://readdy.ai/api/search-image?query=Elegant%20smartphone%20displaying%20photo%20editing%20interface%20on%20a%20clean%20desk%2C%20warm%20ambient%20lighting%2C%20minimalist%20editorial%20product%20photography&width=800&height=500&seq=cat-article-edit&orientation=landscape",
  "zipai-dengguang-buzhi-jiaocheng": "https://readdy.ai/api/search-image?query=Professional%20ring%20light%20and%20photography%20lighting%20setup%20in%20a%20minimal%20studio%2C%20warm%20golden%20glow%2C%20elegant%20product%20photography%20style&width=800&height=500&seq=cat-article-light&orientation=landscape",
  "guochan-shouji-meiyan-shezhi": "https://readdy.ai/api/search-image?query=Elegant%20cosmetic%20and%20beauty%20products%20arranged%20on%20a%20minimal%20vanity%20desk%2C%20warm%20soft%20lighting%2C%20refined%20editorial%20product%20photography&width=800&height=500&seq=cat-article-beauty&orientation=landscape",
  "yeshi-zipai-jiqiao": "https://readdy.ai/api/search-image?query=Elegant%20night%20cityscape%20with%20warm%20neon%20lights%2C%20minimalist%20urban%20photography%2C%20soft%20bokeh%20effects%2C%20warm%20amber%20and%20rose%20tones%2C%20refined%20editorial%20atmosphere&width=800&height=500&seq=cat-article-night&orientation=landscape",
  "jixiang-zu-jia-zipai-weizhi": "https://readdy.ai/api/search-image?query=Beautiful%20Chinese%20landscape%20with%20traditional%20architecture%20and%20natural%20scenery%2C%20warm%20golden%20sunset%20light%2C%20elegant%20travel%20photography%2C%20minimalist%20composition&width=800&height=500&seq=cat-article-travel&orientation=landscape",
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 6;

  const category = useMemo(() => categories.find((c) => c.slug === slug), [slug]);

  if (!category) {
    return <Navigate to="/" replace />;
  }

  const categoryArticles = articles.filter((a) => a.category === slug);
  const totalPages = Math.ceil(categoryArticles.length / pageSize);
  const pagedArticles = categoryArticles.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  return (
    <>
      <main>
        {/* Category Header */}
        <section className="pt-24 pb-10 md:pb-14 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: "首页", href: "/" },
                { label: category.name },
              ]}
            />
            <div className="mt-6 flex items-center gap-4">
              <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-xl bg-accent-100/80">
                <i className={`${category.icon} text-2xl md:text-3xl text-accent-600`}></i>
              </div>
              <div>
                <h1 className="font-heading text-2xl md:text-3xl text-foreground-900">
                  {category.name}
                </h1>
                <p className="text-sm text-foreground-600 mt-1">{category.description}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="sticky top-16 z-30 bg-background-50/95 backdrop-blur-md border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-1 py-3 overflow-x-auto flex-nowrap">
              <Link
                to="/"
                className="px-4 py-2 rounded-full text-sm font-medium text-foreground-600 hover:text-foreground-900 hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
              >
                全部
              </Link>
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    cat.slug === slug
                      ? "bg-primary-500 text-background-50"
                      : "text-foreground-600 hover:text-foreground-900 hover:bg-background-100"
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Articles List */}
        <section className="py-10 md:py-14 px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            {pagedArticles.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200/60 mb-4">
                  <i className="ri-file-search-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-600">该分类暂无文章</p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {pagedArticles.map((article) => (
                    <Link
                      key={article.slug}
                      to={`/article/${article.slug}`}
                      className="group rounded-xl bg-background-50 border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer"
                    >
                      <div className="relative h-48 overflow-hidden">
                        <img
                          src={articleCategoryImages[article.slug] || ""}
                          alt={article.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                      </div>
                      <div className="p-5">
                        <div className="flex items-center gap-3 mb-3">
                          <span className="text-xs text-foreground-500">{article.readTime}</span>
                        </div>
                        <h3 className="font-heading text-base text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                          {article.title}
                        </h3>
                        <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">
                          {article.excerpt}
                        </p>
                        <div className="flex items-center gap-2 mt-4 text-xs text-foreground-500">
                          <span>{article.author}</span>
                          <span>·</span>
                          <span>{article.date}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>

                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-10">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                      <button
                        key={p}
                        onClick={() => {
                          setCurrentPage(p);
                          window.scrollTo({ top: 0, behavior: "instant" });
                        }}
                        className={`w-9 h-9 flex items-center justify-center rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                          p === currentPage
                            ? "bg-primary-500 text-background-50"
                            : "text-foreground-600 hover:bg-background-100"
                        }`}
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      </main>

      {/* Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": `${category.name} - 国产自拍完全指南`,
            "description": category.description,
            "url": `https://njaierbei.cn/category/${category.slug}`,
            "inLanguage": "zh-CN",
            "isPartOf": {
              "@type": "WebSite",
              "name": "国产自拍完全指南",
              "url": "https://njaierbei.cn",
            },
          }),
        }}
      />
    </>
  );
}